import React, { useEffect, useMemo, useState } from "react";
import { adminApiRequest } from "../utils/adminApi";

const initialForm = {
  name: "",
  is_active: true,
};

export default function CategoryModal({
  isOpen,
  onClose,
  onSaved,
  editingCategory = null,
}) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const isEditMode = useMemo(
    () => Boolean(editingCategory?.id),
    [editingCategory],
  );

  useEffect(() => {
    if (!isOpen) return;

    if (editingCategory) {
      setForm({
        name: editingCategory.name || "",
        is_active: Number(editingCategory.is_active) === 1,
      });
    } else {
      setForm(initialForm);
    }

    setSaving(false);
    setError("");
  }, [isOpen, editingCategory]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name.trim()) {
      setError("Category name is required.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = {
        script: isEditMode ? "update_shop_category" : "create_shop_category",
        data: {
          name: form.name.trim(),
          is_active: form.is_active ? 1 : 0,
        },
      };

      if (isEditMode) {
        payload.data.category_id = editingCategory.id;
      }

      const res = await adminApiRequest(payload);

      if (!res?.success) {
        throw new Error(
          res?.message ||
            `Failed to ${isEditMode ? "update" : "create"} category.`,
        );
      }

      await onSaved();
      onClose();
    } catch (err) {
      setError(
        err.message ||
          `Unable to ${isEditMode ? "update" : "create"} category.`,
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-xl overflow-hidden rounded-3xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <h3 className="text-xl font-semibold text-slate-900">
              {isEditMode ? "Edit Category" : "Add Category"}
            </h3>
            <p className="mt-1 text-sm text-slate-500">
              Fill in the category details below.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
          >
            Close
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5">
            {error && (
              <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            )}

            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Category Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-slate-900"
                  placeholder="Enter category name"
                />
              </div>

              <div>
                <label className="inline-flex items-center gap-3">
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={form.is_active}
                    onChange={handleChange}
                    className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
                  />
                  <span className="text-sm text-slate-700">Active</span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3 border-t border-slate-200 px-6 py-4 sm:flex-row sm:items-center sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving
                ? isEditMode
                  ? "Updating..."
                  : "Saving..."
                : isEditMode
                  ? "Update Category"
                  : "Save Category"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}